import EmailApprovalIcon from '@carsys/ui/src/icons/EmailApprovalIcon'
import { Feature } from '@cbgms/base/feature-flags'
import ProductGroupIcon from '@mui/icons-material/AccountTree'
import CompanyIcon from '@mui/icons-material/Apartment'
import ThemeIcon from '@mui/icons-material/Brush'
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard'
import ExternalGMSIcon from '@mui/icons-material/ElectricalServices'
import Email from '@mui/icons-material/Email'
import HolidayIcon from '@mui/icons-material/EventNote'
import GroupIcon from '@mui/icons-material/Group'
import ConceptIcon from '@mui/icons-material/HomeWork'
import PackageIcon from '@mui/icons-material/Inventory'
import WholesalerIcon from '@mui/icons-material/LocalGroceryStore'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread'
import MessageIcon from '@mui/icons-material/Message'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import StatsIcon from '@mui/icons-material/QueryStats'
import CacheIcon from '@mui/icons-material/Save'
import Shop2Icon from '@mui/icons-material/Shop2'
import RegionIcon from '@mui/icons-material/SouthAmerica'
import ShopIcon from '@mui/icons-material/Store'
import TelesalesIcon from '@mui/icons-material/SupportAgent'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import BeenhereIcon from '@mui/icons-material/Beenhere'
import { Box, List, ListItem, listItemButtonClasses } from '@mui/material'
import HasConversionPermission from 'app-support/conversion/HasConversionPermission'
import {
  MODULE_MANAGEMENT_FLAG_MANAGEMENT,
  PERMISSION_ADMIN_UI_CONFIG,
  PERMISSION_APP_ACCESS,
  PERMISSION_CACHE,
  PERMISSION_CUSTOMER_ALERTS,
  PERMISSION_CUSTOMER_EMAIL_ATTACHMENT,
  PERMISSION_CUSTOMER_EMAIL_CONTENT,
  PERMISSION_CUSTOMER_OFFER,
  PERMISSION_CUSTOMER_PENDING_EMAIL,
  PERMISSION_CUSTOMER_PROFILE,
  PERMISSION_DEV_SETTINGS,
  PERMISSION_LESSOR_AND_CONCEPT,
  PERMISSION_MANAGE_BANK_HOLIDAYS,
  PERMISSION_MANAGE_COMPANIES,
  PERMISSION_MANAGE_CONCEPTS,
  PERMISSION_MANAGE_CONCEPT_CONFIGURATION,
  PERMISSION_MANAGE_EXTERNAL_GMS,
  PERMISSION_MANAGE_LICENSES,
  PERMISSION_MANAGE_MESSAGES,
  PERMISSION_MANAGE_PRODUCT_GROUPS,
  PERMISSION_MANAGE_REGIONS,
  PERMISSION_MANAGE_ROAD_ASSISTANCE,
  PERMISSION_MANAGE_SHOPS,
  PERMISSION_MANAGE_STATS,
  PERMISSION_MANAGE_THEMES,
  PERMISSION_MANAGE_WARRANTY,
  PERMISSION_MANAGE_WHOLESALERS,
  PERMISSION_NEW_CUSTOMERT_ACQUISITION,
  PERMISSION_TELESALES,
  PERMISSION_TROUBLESHOOT
} from 'app-support/permissions/Permissions'
import { RequiredPermission } from 'app-support/permissions/RequiredPermissions'
import lkqLogoURL from 'assets/svg/LKQLogoWhite.svg'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { developerSettingsDashboardRoute } from 'routes/routeNames'
import { IRootReducerState } from 'state/reducers'
import RoadAssistanceIcon from '../../assets/icon-components/road-assistance-icon'
import { CollapseableMenuItem } from './components/CollapsebleMenuItem'
import MenuItem from './components/MenuItem'
import { useAppURLs } from './hooks/use-app-urls'
import { useTokenRefresh } from './hooks/use-token-refresh'
import ConceptConfigurationIcon from 'assets/icon-components/concept-configuration-icon'
import HasConceptConfigurationPermission from 'app-support/concept-configuration/HasConceptConfigurationPermission'
import HasRegionPermission from 'app-support/permissions/HasRegionPermission'

const WARRANTY_REGIONS = ['nl', 'fr'] as const
const WHOLESALE_FORMS_REGIONS = ['nl', 'fr'] as const

const Sidebar: React.FC<{}> = () => {
  const { t } = useTranslation()
  const [selectedID, setSelectedID] = React.useState<string>()

  const emailAddress = useSelector((state: IRootReducerState) => state.auth.user.email)
  const refreshToken = useSelector((state: IRootReducerState) => state.auth.tokens.RefreshToken)
  const tokenRefresh = useTokenRefresh()
  const appURLs = useAppURLs()
  const disableMenuLinks = tokenRefresh.isLoading

  const handleExternalItemClick = async (url: string) => {
    tokenRefresh.mutate(
      { emailAddress, refreshToken },
      {
        onSuccess: () => {
          const finalUrl = url.replace('[email]', emailAddress).replace('[refreshToken]', refreshToken)
          window.open(finalUrl, '_blank')
        }
      }
    )
  }

  const handleCollapseID = (ID?: string) => {
    if (selectedID === ID) {
      setSelectedID(undefined)
    } else {
      setSelectedID(ID)
    }
  }

  return (
    <List
      sx={{
        padding: 0,
        [`& .${listItemButtonClasses.root}`]: {
          backgroundColor: '#000'
        },
        [`& .${listItemButtonClasses.root}:hover`]: {
          backgroundColor: '#333'
        }
      }}
      component='div'
    >
      <ListItem
        data-testid='side-menu-logo'
        to='/'
        component={Link}
        sx={{
          borderBottom: '3px solid #085CF0',
          color: theme => theme.palette.common.white,
          backgroundColor: theme => theme.palette.common.black,
          height: '75px'
        }}
      >
        <Box component='img' src={lkqLogoURL} />
      </ListItem>

      <RequiredPermission permission={PERMISSION_APP_ACCESS}>
        <CollapseableMenuItem
          selectedID={selectedID}
          collapseID='companies'
          handleCollapse={handleCollapseID}
          parentItemText={t('global.menu_companies')}
        >
          <MenuItem
            subItem
            url='/companies'
            Icon={<CompanyIcon />}
            text={t('global.menu_companies')}
            permissions={PERMISSION_MANAGE_COMPANIES}
          />
          <MenuItem subItem url='/shops' Icon={<ShopIcon />} text={t('global.menu_shops')} permissions={PERMISSION_MANAGE_SHOPS} />
          <MenuItem subItem url='/stats' Icon={<StatsIcon />} text={t('global.statistics')} permissions={PERMISSION_MANAGE_STATS} />
          <HasConversionPermission>
            <MenuItem subItem url='/conversion' Icon={<Shop2Icon />} text={t('global.conversion')} />
          </HasConversionPermission>
          <CollapseableMenuItem subItem parentItemText={t('support.menu_troubleshoot')}>
            <MenuItem subItem url='/troubleshoot/accountancy' text={t('global.accountancy')} permissions={PERMISSION_TROUBLESHOOT} />
            <MenuItem
              subItem
              url='/troubleshoot/default-revenue-group'
              text={t('global.default-revenue-group')}
              permissions={PERMISSION_TROUBLESHOOT}
            />
            <MenuItem
              subItem
              url='/troubleshoot/accountancy-software'
              text={t('global.administration_filter')}
              permissions={PERMISSION_TROUBLESHOOT}
            />
          </CollapseableMenuItem>
        </CollapseableMenuItem>

        <MenuItem url='/telesales' Icon={<TelesalesIcon />} text={t('global.menu_telesales')} permissions={PERMISSION_TELESALES} />

        <CollapseableMenuItem
          parentItemText={t('support.menu_communication')}
          selectedID={selectedID}
          collapseID='communication'
          handleCollapse={handleCollapseID}
        >
          <MenuItem subItem url='/messages' Icon={<MessageIcon />} text={t('global.messages')} permissions={PERMISSION_MANAGE_MESSAGES} />
        </CollapseableMenuItem>

        <CollapseableMenuItem
          parentItemText={t('support.menu_catalogs')}
          selectedID={selectedID}
          collapseID='catalog'
          handleCollapse={handleCollapseID}
        >
          <MenuItem
            subItem
            url='/wholesale'
            Icon={<WholesalerIcon />}
            text={t('global.menu_wholesale')}
            permissions={PERMISSION_MANAGE_WHOLESALERS}
          />
          <HasRegionPermission allowedRegions={WARRANTY_REGIONS}>
            <MenuItem
              subItem
              url='/warranty'
              Icon={<BeenhereIcon />}
              text={t('global.menu_warranty')}
              permissions={PERMISSION_MANAGE_WARRANTY}
            />
          </HasRegionPermission>
          <HasRegionPermission allowedRegions={WHOLESALE_FORMS_REGIONS}>
            <Feature feature='axnl-refurb-request'>
              <MenuItem
                subItem
                url='/wholesale-forms'
                Icon={<InsertDriveFileIcon />}
                text={t('global.menu_document')}
                permissions={PERMISSION_MANAGE_WHOLESALERS}
              />
            </Feature>
          </HasRegionPermission>
          <MenuItem
            subItem
            permissions={PERMISSION_LESSOR_AND_CONCEPT}
            disabled={disableMenuLinks}
            text={t('support.spot_on_admin')}
            onClick={() => handleExternalItemClick(appURLs.spotOn)}
          />
          <MenuItem
            subItem
            url='/product-group'
            Icon={<ProductGroupIcon />}
            text={t('global.menu_productgroup')}
            permissions={PERMISSION_MANAGE_PRODUCT_GROUPS}
          />
          <MenuItem
            subItem
            permissions={PERMISSION_LESSOR_AND_CONCEPT}
            disabled={disableMenuLinks}
            text={t('support.jobs_admin')}
            onClick={() => handleExternalItemClick(appURLs.jobs)}
          />
        </CollapseableMenuItem>

        <CollapseableMenuItem
          parentItemText={t('support.external_applications')}
          selectedID={selectedID}
          collapseID='external-application'
          handleCollapse={handleCollapseID}
        >
          <MenuItem
            subItem
            url='/concepts'
            Icon={<ConceptIcon />}
            text={t('global.menu_concepts')}
            permissions={PERMISSION_MANAGE_CONCEPTS}
          />
          <HasConceptConfigurationPermission>
            <MenuItem
              subItem
              url='/concept-configuration'
              Icon={<ConceptConfigurationIcon />}
              text={t('global.concept_configuration')}
              permissions={PERMISSION_MANAGE_CONCEPT_CONFIGURATION}
            />
          </HasConceptConfigurationPermission>
          <MenuItem
            subItem
            permissions={PERMISSION_LESSOR_AND_CONCEPT}
            disabled={disableMenuLinks}
            text={t('support.concept_admin')}
            onClick={() => handleExternalItemClick(appURLs.conceptAdmin)}
          />
          <MenuItem
            subItem
            permissions={PERMISSION_LESSOR_AND_CONCEPT}
            disabled={disableMenuLinks}
            text={t('support.lessor_admin')}
            onClick={() => handleExternalItemClick(appURLs.lessorAdmin)}
          />
          <MenuItem
            subItem
            permissions={PERMISSION_LESSOR_AND_CONCEPT}
            disabled={disableMenuLinks}
            text={t('support.tenants_admin')}
            onClick={() => handleExternalItemClick(appURLs.tenants)}
          />
          <MenuItem
            subItem
            url='/external-gms'
            Icon={<ExternalGMSIcon />}
            text={t('global.menu_external_gms')}
            permissions={PERMISSION_MANAGE_EXTERNAL_GMS}
          />
          <MenuItem
            subItem
            url='/road-assistance'
            Icon={<RoadAssistanceIcon />}
            text={t('global.menu_road_assistance_gms')}
            permissions={PERMISSION_MANAGE_ROAD_ASSISTANCE}
          />
        </CollapseableMenuItem>

        <CollapseableMenuItem
          parentItemText={t('support.menu_licenses')}
          selectedID={selectedID}
          collapseID='licenses'
          handleCollapse={handleCollapseID}
        >
          <MenuItem subItem permissions={PERMISSION_MANAGE_LICENSES} url='/licenses/distributors' text={t('support.menu_distributors')} />
          <MenuItem subItem permissions={PERMISSION_MANAGE_LICENSES} url='/licenses/haynespro' text={t('support.menu_haynespro')} />
        </CollapseableMenuItem>
        <Feature feature='admin-ui-customer'>
          <CollapseableMenuItem
            parentItemText={t('support.menu_customer')}
            selectedID={selectedID}
            collapseID='customer'
            handleCollapse={handleCollapseID}
          >
            <Feature feature='admin-ui-offerpackage'>
              <MenuItem
                subItem
                permissions={PERMISSION_NEW_CUSTOMERT_ACQUISITION}
                url='/LKQ/OfferPackage'
                Icon={<PackageIcon />}
                text={t('global.offerpackage')}
              />
            </Feature>
            <Feature feature='admin-ui-profile'>
              <MenuItem
                subItem
                permissions={PERMISSION_CUSTOMER_PROFILE}
                url='/LKQ/Profile'
                Icon={<PersonSearchIcon />}
                text={t('global.profile')}
              />
            </Feature>

            <MenuItem
              subItem
              url='/LKQ/EmailAttachment'
              permissions={PERMISSION_CUSTOMER_EMAIL_ATTACHMENT}
              Icon={<EmailApprovalIcon />}
              text={t('global.email_attachment')}
            />

            <Feature feature='admin-ui-modulemanagement'>
              <MenuItem
                subItem
                permissions={MODULE_MANAGEMENT_FLAG_MANAGEMENT}
                url='/LKQ/ManageCustomerAccess'
                Icon={<ViewModuleIcon />}
                text={t('global.modulemanagement')}
              />
            </Feature>
            <MenuItem
              subItem
              permissions={PERMISSION_CUSTOMER_EMAIL_CONTENT}
              url='/LKQ/EmailContent'
              Icon={<Email />}
              text={t('global.emailContent')}
            />
            <MenuItem
              subItem
              permissions={PERMISSION_CUSTOMER_PENDING_EMAIL}
              url='/LKQ/PendingEmailList'
              Icon={<MarkEmailUnreadIcon />}
              text={t('global.PendingEmailList')}
            />
            <MenuItem
              subItem
              permissions={PERMISSION_CUSTOMER_OFFER}
              url='/LKQ/SalesGroups'
              Icon={<GroupIcon />}
              text={t('global.articleOffer')}
            />
            <MenuItem subItem permissions={PERMISSION_CUSTOMER_ALERTS} url='/LKQ/Alerts' Icon={<MessageIcon />} text={t('Alert.Alerts')} />
            <MenuItem
              subItem
              permissions={PERMISSION_ADMIN_UI_CONFIG}
              url='/LKQ/Settings'
              Icon={<GroupIcon />}
              text={t('LKQConfig.Menu')}
            />
          </CollapseableMenuItem>
        </Feature>
        <CollapseableMenuItem
          parentItemText={t('support.menu_settings')}
          selectedID={selectedID}
          collapseID='settings'
          handleCollapse={handleCollapseID}
        >
          <MenuItem subItem url='/themes' Icon={<ThemeIcon />} text={t('global.menu_themes')} permissions={PERMISSION_MANAGE_THEMES} />
          <MenuItem
            subItem
            url='/regions'
            Icon={<RegionIcon />}
            text={t('bank_holidays.regions')}
            permissions={PERMISSION_MANAGE_REGIONS}
          />
          <MenuItem
            subItem
            url='/bank-holidays'
            Icon={<HolidayIcon />}
            text={t('bank_holidays.title')}
            permissions={PERMISSION_MANAGE_BANK_HOLIDAYS}
          />
          <CollapseableMenuItem subItem parentItemText={t('support.menu_developer_tools')}>
            <MenuItem url='/cache' subItem Icon={<CacheIcon />} text={t('global.menu_cache')} permissions={PERMISSION_CACHE} />
            <MenuItem
              url={developerSettingsDashboardRoute}
              subItem
              Icon={<DeveloperBoardIcon />}
              text={t('global.dev-dashboard')}
              permissions={PERMISSION_DEV_SETTINGS}
            />
          </CollapseableMenuItem>
        </CollapseableMenuItem>
      </RequiredPermission>
    </List>
  )
}

Sidebar.displayName = 'Sidebar'
export default Sidebar
